import { AppLayout } from "@/components/AppLayout";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Star, Users, FileText, Briefcase, Award } from "lucide-react";

export default function Services() {
  const services = [
    {
      id: "1",
      title: "Full Resume Rebuild",
      description: "Perfect if you're starting fresh or need to completely reposition your brand.",
      icon: FileText,
      features: [
        "Complete formatting upgrade",
        "Industry-specific language", 
        "Compelling summary + optimized bullet points",
        "Keyword tailoring for ATS",
        "1-on-1 consultation included"
      ],
      highlight: false
    },
    {
      id: "2", 
      title: "Resume Quantification & Metrics Enhancement",
      description: "Already have a resume but lacking strong numbers? We'll inject performance data and measurable results using our Metrics Wizard + expert editing.",
      icon: Award,
      features: [
        "Powerful, quantified bullet points",
        "Strong action verbs",
        "Industry-specific metrics prompts", 
        "Revised to show value, not just tasks"
      ],
      highlight: true
    },
    {
      id: "3",
      title: "Job-Specific Tailoring", 
      description: "Applying to a specific job? Send us the job description—we'll match your resume to the language, values, and requirements of that exact role.",
      icon: Star,
      features: [
        "ATS optimization",
        "Keyword alignment",
        "Language mirroring for better relevance",
        "Optional cover letter add-on"
      ],
      highlight: false
    },
    {
      id: "4",
      title: "LinkedIn Profile Optimization",
      description: "Boost your personal brand and visibility with a polished LinkedIn profile that speaks directly to recruiters.",
      icon: Users,
      features: [
        "Headline + summary refresh",
        "Keyword optimization", 
        "Achievement-focused language",
        "Consistency with resume"
      ],
      highlight: false
    },
    {
      id: "5",
      title: "Executive Resume Services",
      description: "For Director-level and above. High-impact storytelling, leadership framing, and board-level language that sets you apart in competitive markets.",
      icon: Briefcase,
      features: [
        "Executive branding",
        "Leadership metrics and narratives",
        "Clean, modern executive formatting"
      ],
      highlight: false
    }
  ];

  const processSteps = [
    "Choose Your Service",
    "Submit Your Resume (if applicable) + Job Goal", 
    "Get a Custom Quote & Turnaround Time",
    "Collaborate with Our Expert Team",
    "Receive Your Polished Deliverables"
  ];

  return (
    <AppLayout>
      <PageHeader 
        title="Professional Resume Services" 
        description="Expert resume writing and career services tailored to your goals"
        backTo="/"
      />
      <div className="container mx-auto max-w-6xl px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="text-4xl mb-4">✨</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Available Services
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Professional resume writing services designed to help you land your dream job
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <Card 
                key={service.id} 
                className={`relative ${service.highlight 
                  ? 'bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30 ring-2 ring-primary/20' 
                  : 'bg-gradient-to-br from-card to-muted/20 border-border'
                } hover:shadow-lg transition-all duration-200`}
              >
                {service.highlight && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </div>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      service.highlight ? 'bg-primary text-white' : 'bg-accent text-white'
                    }`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-success" />
                      <span className="text-lg font-bold text-foreground">{service.id}.</span>
                    </div>
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* How It Works */}
        <Card className="mb-16 bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20">
          <CardHeader className="text-center">
            <div className="text-3xl mb-2">📝</div>
            <CardTitle className="text-2xl font-bold">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3">
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-sm text-foreground leading-tight">
                    {step}
                  </h3>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block text-muted-foreground text-2xl mt-2">→</div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 bg-muted/50 rounded-lg border">
              <p className="text-center text-muted-foreground">
                <strong>Every project is done manually by an expert, not just by AI.</strong> Your resume is reviewed line-by-line for strategy, storytelling, and hiring impact.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="bg-gradient-to-br from-primary/5 to-orange-500/5 border-primary/20">
          <CardHeader className="text-center">
            <div className="text-3xl mb-2">🎯</div>
            <CardTitle className="text-2xl font-bold">Ready to Upgrade Your Resume?</CardTitle>
          </CardHeader>
          <CardContent className="text-center pb-8">
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl">👉</span>
                <p className="text-lg">Fill out the short request form below and let us know how we can help.</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <span className="text-2xl">📩</span>
                <p className="text-lg">You'll get a response from a real human within 24 hours.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <a href="mailto:services@tailorwiz.com?subject=Request%20for%20TailorWiz%20Services">
                <Button 
                  size="lg" 
                  className="text-xl px-12 py-6 h-16 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  Request Services Now
                </Button>
              </a>
              <p className="text-sm text-muted-foreground">
                Contact us at{" "}
                <a 
                  href="mailto:services@tailorwiz.com" 
                  className="text-primary underline hover:text-primary/80 font-semibold"
                >
                  services@tailorwiz.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}